/* =========================================================
   BAREAYA – QUICK ADD + STICKY ATC + HEADER CART COUNT SYNC
   - Pas de floating cart
   - 1 seule logique de refresh
   - Update OK depuis landing / collection / PDP / sticky / ajax theme
   ========================================================= */

const HEADER_COUNT_SELECTOR = '.cart-count-header';

/* =========================================================
   UTIL : Update tous les compteurs/bulles panier du header
   - Texte: .cart-link__count, .site-header-mobile__cart-bubble [aria-hidden],
            .floating-cart-count, .cart-count-header
   - Visibilité: classe is-visible / cart-link__bubble--visible / is-zero
   ========================================================= */
function updateAllCartBubbles(itemCount) {
  const count = Number(itemCount) || 0;
  const countStr = String(count);
  const visible = count > 0;

  // Compteurs texte legacy
  document.querySelectorAll(HEADER_COUNT_SELECTOR).forEach((el) => {
    el.textContent = countStr;
    el.classList.toggle('is-zero', count === 0);
  });

  // Header desktop (snippets/header-icons.liquid)
  document.querySelectorAll('.cart-link__count').forEach((el) => {
    el.textContent = countStr;
  });
  document.querySelectorAll('.cart-link__bubble').forEach((el) => {
    el.classList.toggle('cart-link__bubble--visible', visible);
  });

  // Header mobile custom (sections/header.liquid)
  document.querySelectorAll('.site-header-mobile__cart-bubble').forEach((el) => {
    el.classList.toggle('is-visible', visible);
    const inner = el.querySelector('span[aria-hidden], span');
    if (inner) inner.textContent = countStr;
  });

  // Floating / sticky cart legacy
  document.querySelectorAll('.floating-cart-count').forEach((el) => {
    el.textContent = countStr;
  });

  // Body class du thème
  document.body.classList.toggle('cart-has-items', visible);
}

/* =========================================================
   UTIL : Refresh header cart counter (tous les compteurs header)
   ========================================================= */
async function refreshHeaderCartCount() {
  try {
    const res = await fetch('/cart.js', { credentials: 'same-origin' });
    if (!res.ok) return;

    const cart = await res.json();
    updateAllCartBubbles(cart.item_count);
  } catch (e) {
    // optionnel: console.warn('refreshHeaderCartCount failed', e);
  }
}

/* =========================================================
   UTIL : Refresh header + cart drawer via Shopify sections
   NOTE: on fetch "/" pour avoir le header "global" fiable
   - cart-drawer est un SNIPPET (pas une section) inclus dans
     layout/theme.liquid → on doit fetch la page HTML complète
     pour extraire #CartDrawer mis à jour.
   ========================================================= */
async function refreshCartDrawerHTML() {
  try {
    const res = await fetch('/', {
      credentials: 'same-origin',
      headers: { Accept: 'text/html' },
      cache: 'no-store',
    });
    if (!res.ok) return;
    const html = await res.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const newDrawer = doc.querySelector('#CartDrawer');
    const oldDrawer = document.querySelector('#CartDrawer');
    if (newDrawer && oldDrawer) {
      // Préserve les classes d'état (drawer--is-open) si déjà ouvert
      const wasOpen = oldDrawer.classList.contains('drawer--is-open');
      const ariaHidden = oldDrawer.getAttribute('aria-hidden');
      oldDrawer.innerHTML = newDrawer.innerHTML;
      // Met à jour la classe drawer--cart--static selon nb d'items
      newDrawer.classList.forEach((c) => {
        if (c.startsWith('drawer--cart')) {
          oldDrawer.classList.remove('drawer--cart--static');
          if (c === 'drawer--cart--static') oldDrawer.classList.add(c);
        }
      });
      if (wasOpen) {
        oldDrawer.classList.add('drawer--is-open');
        if (ariaHidden !== null) oldDrawer.setAttribute('aria-hidden', ariaHidden);
      }
    }
  } catch (e) {
    // ignore
  }
}

async function refreshCartSections() {
  try {
    const res = await fetch(`/?sections=header`, { credentials: 'same-origin' });
    if (!res.ok) return;

    const sections = await res.json();

    // Header
    if (sections.header) {
      const headerDoc = new DOMParser().parseFromString(sections.header, 'text/html');
      const newHeader = headerDoc.querySelector('#shopify-section-header');
      const oldHeader = document.querySelector('#shopify-section-header');
      if (newHeader && oldHeader) {
        oldHeader.innerHTML = newHeader.innerHTML;
      }
    }
  } catch (e) {
    // optionnel: console.warn('refreshCartSections failed', e);
  }
}

/* =========================================================
   UI Sync : ordre IMPORTANT
   - Sections d’abord (remplace le header)
   - Puis compteur (écrit sur le nouveau header)
   ========================================================= */
async function syncCartUI() {
  // Header (section) + Drawer (snippet via fetch HTML) en parallèle
  await Promise.all([refreshCartSections(), refreshCartDrawerHTML()]);
  await refreshHeaderCartCount();
}

/* =========================================================
   Ouvre le cart drawer (utilisé après chaque ajout au panier)
   ========================================================= */
function openCartDrawer() {
  var d = document.getElementById('CartDrawer');
  if (!d) return;
  d.classList.add('drawer--is-open');
  d.setAttribute('aria-hidden', 'false');
  document.documentElement.classList.add('js-drawer-open');
  document.body.classList.add('js-drawer-open', 'cart-drawer-open');
}

/* =========================================================
   AJAX ADD TO CART (form)
   ========================================================= */
async function ajaxAddToCart(form, button) {
  const originalText = button ? button.textContent : null;

  try {
    if (button) {
      button.disabled = true;
      button.classList.add('is-loading');
      if (originalText) button.textContent = 'Ajout...';
    }

    const formData = new FormData(form);

    const res = await fetch('/cart/add.js', {
      method: 'POST',
      body: formData,
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });

    if (!res.ok) throw new Error('Add to cart failed');

    // Update bulle instantanément (sans attendre la sync sections)
    refreshHeaderCartCount();

    await syncCartUI();

    // Ouvre le cart drawer après ajout
    openCartDrawer();

    if (button && originalText) {
      button.textContent = 'Ajouté ✓';
      setTimeout(() => (button.textContent = originalText), 900);
    }
  } catch (e) {
    console.error(e);
    if (button && originalText) button.textContent = originalText;
  } finally {
    if (button) {
      button.disabled = false;
      button.classList.remove('is-loading');
    }
  }
}

/* =========================================================
   QUICK ADD – Collection + Featured
   Cible uniquement form.quick-add-form
   ========================================================= */
document.addEventListener(
  'submit',
  function (e) {
    const form = e.target;
    if (!form || !form.matches) return;
    if (!form.matches('form.quick-add-form')) return;

    e.preventDefault();
    e.stopPropagation();
    if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();

    const button = form.querySelector('button[type="submit"], input[type="submit"]');
    ajaxAddToCart(form, button);
  },
  true
);

/* =========================================================
   Empêche le lien parent des tuiles de capter le clic
   (sans bloquer le submit)
   ========================================================= */
document.addEventListener(
  'click',
  function (e) {
    const btn = e.target.closest('.quick-add-button');
    if (!btn) return;

    e.stopPropagation();
    if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
  },
  true
);

/* =========================================================
   STICKY ADD TO CART – Page Produit
   ========================================================= */
function bindStickyATC() {
  const stickyBtn = document.querySelector('.sticky-atc button, .sticky-add-to-cart button, #StickyATC button');
  if (!stickyBtn || stickyBtn.__bareayaBound) return;
  stickyBtn.__bareayaBound = true;

  stickyBtn.addEventListener(
    'click',
    async function (e) {
      e.preventDefault();

      const form =
        document.querySelector('form[action="/cart/add"]') ||
        document.querySelector('form.product-single__form') ||
        document.querySelector('form.product-form');

      if (!form) return;

      const idInput = form.querySelector('input[name="id"]');
      if (!idInput || !idInput.value) return;

      stickyBtn.classList.add('is-loading');

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: idInput.value, quantity: 1 }),
        });

        if (!res.ok) throw new Error('Sticky add failed');

        await syncCartUI();
        openCartDrawer();
      } catch (e) {
        console.error(e);
      } finally {
        stickyBtn.classList.remove('is-loading');
      }
    },
    { passive: false }
  );
}

/* =========================================================
   INIT
   ========================================================= */
document.addEventListener('DOMContentLoaded', function () {
  refreshHeaderCartCount();
  bindStickyATC();
});

document.addEventListener('shopify:section:load', function () {
  // après un rerender, rebinder + rafraîchir
  bindStickyATC();
  refreshHeaderCartCount();
});

document.addEventListener('variant:changed', function () {
  bindStickyATC();
});

/* =========================================================
   SAFETY NET – Catch all cart requests (theme AJAX)
   - couvre tous les /cart/add /cart/change /cart/update
   ========================================================= */
(function () {
  let refreshTimer = null;

  function scheduleSync() {
    if (refreshTimer) clearTimeout(refreshTimer);

    // 1) synchro rapide
    refreshTimer = setTimeout(() => {
      syncCartUI().then(openCartDrawer);
    }, 120);

    // 2) une passe plus tardive (thèmes lents)
    setTimeout(refreshHeaderCartCount, 650);
  }

  // Hook fetch
  const _fetch = window.fetch;
  if (_fetch) {
    window.fetch = function () {
      const args = arguments;
      const url = args[0] && args[0].url ? args[0].url : args[0];

      return _fetch.apply(this, args).then((response) => {
        try {
          if (
            typeof url === 'string' &&
            url.includes('/cart/add')
          ) {
            scheduleSync();
          } else if (
            typeof url === 'string' &&
            (url.includes('/cart/change') || url.includes('/cart/update'))
          ) {
            // Sync silencieuse (pas d'ouverture du drawer sur change/update)
            if (refreshTimer) clearTimeout(refreshTimer);
            refreshTimer = setTimeout(() => syncCartUI(), 120);
            setTimeout(refreshHeaderCartCount, 650);
          }
        } catch (e) {}
        return response;
      });
    };
  }

  // Hook XHR
  const _open = XMLHttpRequest.prototype.open;
  const _send = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function (method, url) {
    this.__isCartAdd =
      typeof url === 'string' && url.includes('/cart/add');
    this.__isCartChange =
      typeof url === 'string' &&
      (url.includes('/cart/change') || url.includes('/cart/update'));
    return _open.apply(this, arguments);
  };

  XMLHttpRequest.prototype.send = function () {
    if (this.__isCartAdd) {
      this.addEventListener('loadend', scheduleSync);
    } else if (this.__isCartChange) {
      this.addEventListener('loadend', function () {
        if (refreshTimer) clearTimeout(refreshTimer);
        refreshTimer = setTimeout(() => syncCartUI(), 120);
        setTimeout(refreshHeaderCartCount, 650);
      });
    }
    return _send.apply(this, arguments);
  };

  // Bonus: events éventuels du thème
  document.addEventListener('cart:updated', scheduleSync);
  document.addEventListener('ajaxCart:afterAdd', scheduleSync);
  document.addEventListener('theme:cart:updated', scheduleSync);
})();
/* =========================================================
   HARD REMOVE FLOATING CART (ALL DEVICES)
   ========================================================= */
(function () {

  function removeFloatingCart() {
    const selectors = [
      '[id*="FloatingCart" i]',
      '[class*="floating-cart" i]',
      '[class*="floatingcart" i]',
      '[class*="float-cart" i]',
      '[class*="cart-float" i]',
      '[class*="sticky-cart" i]',
      '[data-floating-cart]',
      '[data-float-cart]'
    ];

    document.querySelectorAll(selectors.join(',')).forEach((el) => {
      el.remove();
    });
  }

  document.addEventListener('DOMContentLoaded', removeFloatingCart);
  document.addEventListener('shopify:section:load', removeFloatingCart);

  // Sécurité pendant 3 secondes (thèmes qui injectent tard)
  document.addEventListener('DOMContentLoaded', () => {
    const start = Date.now();
    const interval = setInterval(() => {
      removeFloatingCart();
      if (Date.now() - start > 3000) clearInterval(interval);
    }, 250);
  });

})();